/**
 * Countdown Timer — live countdown to a target date.
 *
 * Defines the `<fit-countdown-timer>` custom element.
 *
 * Configuration (data attributes on the host element):
 *   data-target-date   — ISO date string for the countdown deadline.
 *                        Timezone-less values are interpreted as UTC.
 *   data-expired-text  — Message displayed after the deadline passes.
 *   data-days-label    — Label for the days unit (used in aria announcements).
 *   data-hours-label   — Label for the hours unit.
 *   data-minutes-label — Label for the minutes unit.
 *   data-seconds-label — Label for the seconds unit.
 *   data-remaining-label — Localized suffix for active aria announcements.
 *   data-complete-label — Localized fallback aria label after expiry.
 *   data-invalid-text  — Localized visible fallback for blank / invalid target.
 *   data-invalid-label — Localized aria label for blank / invalid target.
 *
 * Child element contract (queried via data selectors):
 *   [data-countdown-display] — wrapper that is replaced on expiry / invalid.
 *   [data-days]              — element whose textContent receives padded days.
 *   [data-hours]             — element whose textContent receives padded hours.
 *   [data-minutes]           — element whose textContent receives padded minutes.
 *   [data-seconds]           — element whose textContent receives padded seconds.
 *
 * Accessibility:
 *   - Host has role="timer" and aria-live="polite" (set in Liquid).
 *   - Initial aria-label describes the timer purpose (set in Liquid).
 *   - Screen reader announcement fires once per minute via aria-label update
 *     to avoid excessive announcements on every tick.
 *   - On expiry the aria-label switches to the expired message.
 *   - Invalid / blank dates show a deterministic fallback — never NaN.
 */
(function () {
  'use strict';

  if (customElements.get('fit-countdown-timer')) return;

  var MS_SEC = 1000;
  var MS_MIN = MS_SEC * 60;
  var MS_HR  = MS_MIN * 60;
  var MS_DAY = MS_HR  * 24;

  /** Zero-pad a non-negative integer to at least 2 digits. */
  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /**
   * Normalize merchant-entered dates so deadline math is deterministic across
   * shopper timezones. Explicit timezone offsets are preserved.
   */
  function normalizeTargetDate(raw) {
    var value = String(raw || '').trim();
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value + 'T00:00:00Z';
    if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) return value;
    return value + 'Z';
  }

  class FitCountdownTimer extends HTMLElement {
    /** @type {number|null} */
    #timer = null;
    /** @type {number} deadline epoch ms — NaN when invalid */
    #deadline = NaN;
    /** @type {{ d: Element|null, h: Element|null, m: Element|null, s: Element|null }} */
    #els = { d: null, h: null, m: null, s: null };
    /** @type {number} tracks last announced minute to debounce SR updates */
    #lastAnnouncedMinute = -1;

    connectedCallback() {
      var raw = this.dataset.targetDate;
      this.#deadline = raw ? new Date(normalizeTargetDate(raw)).getTime() : NaN;

      this.#els = {
        d: this.querySelector('[data-days]'),
        h: this.querySelector('[data-hours]'),
        m: this.querySelector('[data-minutes]'),
        s: this.querySelector('[data-seconds]'),
      };

      /* If the date is blank or unparseable, render a safe fallback. */
      if (Number.isNaN(this.#deadline)) {
        this.#showInvalid();
        return;
      }

      this.#tick();
      this.#timer = setInterval(() => this.#tick(), MS_SEC);
    }

    disconnectedCallback() {
      if (this.#timer !== null) {
        clearInterval(this.#timer);
        this.#timer = null;
      }
    }

    #tick() {
      var gap = this.#deadline - Date.now();

      if (gap < 0) {
        this.#stop();
        this.#expired();
        return;
      }

      var days    = Math.floor(gap / MS_DAY);
      var hours   = Math.floor((gap % MS_DAY) / MS_HR);
      var minutes = Math.floor((gap % MS_HR)  / MS_MIN);
      var seconds = Math.floor((gap % MS_MIN) / MS_SEC);

      if (this.#els.d) this.#els.d.textContent = pad(days);
      if (this.#els.h) this.#els.h.textContent = pad(hours);
      if (this.#els.m) this.#els.m.textContent = pad(minutes);
      if (this.#els.s) this.#els.s.textContent = pad(seconds);

      /* Update aria-label once per minute to avoid SR spam. */
      var currentMinute = days * 1440 + hours * 60 + minutes;
      if (currentMinute !== this.#lastAnnouncedMinute) {
        this.#lastAnnouncedMinute = currentMinute;
        this.#announce(days, hours, minutes, seconds);
      }
    }

    /**
     * Build a human-readable aria-label reflecting only visible units.
     * @param {number} d - days remaining
     * @param {number} h - hours remaining
     * @param {number} m - minutes remaining
     * @param {number} s - seconds remaining
     */
    #announce(d, h, m, s) {
      var parts = [];
      if (this.#els.d) parts.push(d + ' ' + (this.dataset.daysLabel || ''));
      if (this.#els.h) parts.push(h + ' ' + (this.dataset.hoursLabel || ''));
      if (this.#els.m) parts.push(m + ' ' + (this.dataset.minutesLabel || ''));
      if (this.#els.s) parts.push(s + ' ' + (this.dataset.secondsLabel || ''));
      if (parts.length) {
        this.setAttribute('aria-label', parts.join(', ') + ' ' + (this.dataset.remainingLabel || ''));
      }
    }

    #expired() {
      var wrap = this.querySelector('[data-countdown-display]');
      var msg  = this.dataset.expiredText;

      if (msg && wrap) {
        var el = document.createElement('div');
        el.className = 'fit-cd-expired';
        el.textContent = msg;
        wrap.textContent = '';
        wrap.appendChild(el);
      } else {
        /* Fallback: zero out all visible units. */
        for (var key in this.#els) {
          if (this.#els[key]) this.#els[key].textContent = '00';
        }
      }

      this.setAttribute('aria-label', msg || this.dataset.completeLabel || '');
    }

    /**
     * Render a deterministic fallback when target_date is blank or
     * malformed, so the component never shows NaN.
     */
    #showInvalid() {
      var wrap = this.querySelector('[data-countdown-display]');
      if (wrap) {
        var el = document.createElement('div');
        el.className = 'fit-cd-invalid';
        el.textContent = this.dataset.invalidText || '';
        wrap.textContent = '';
        wrap.appendChild(el);
      }
      this.setAttribute('aria-label', this.dataset.invalidLabel || '');
    }

    #stop() {
      if (this.#timer !== null) {
        clearInterval(this.#timer);
        this.#timer = null;
      }
    }
  }

  customElements.define('fit-countdown-timer', FitCountdownTimer);
})();
